<script setup lang="ts">
import type { ContactModel } from '@/components/fieldsets/StopContactFieldset.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingStop,
  StopHint,
  StopTimeObject,
} from '@/models/advancedJobBooking';

import { computed } from 'vue';

import JobBookingWarningBanner from '@/components/banners/JobBookingWarningBanner.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import StopAttributesFieldset from '@/components/fieldsets/StopAttributesFieldset.vue';
import StopBookingDateTimeFieldset from '@/components/fieldsets/StopBookingDateTimeFieldset.vue';
import StopContactFieldset from '@/components/fieldsets/StopContactFieldset.vue';
import StopCustomerWarehouseFieldset from '@/components/fieldsets/StopCustomerWarehouseFieldset.vue';
import { createEmptyStop } from '@/models/advancedJobBooking';

export interface Props {
  index?: number;
  totalStops?: number;
  customerCode?: string;
  addressItems?: openapi.AddressGETOpenapi[];
  addressLoading?: boolean;
  minTime?: StopTimeObject | null;
  interstate?: boolean;
  customerToCustomer?: boolean;
  hints?: StopHint[];
  deletable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  totalStops: 1,
  customerCode: '',
  addressItems: () => [],
  addressLoading: false,
  minTime: undefined,
  interstate: false,
  customerToCustomer: false,
  hints: () => [],
  deletable: false,
});

const emit = defineEmits<{
  moveLeft: [event: Event];
  moveRight: [event: Event];
  openTimeline: [event: Event];
  delete: [event: Event];
}>();

const stop = defineModel<BookingStop>({
  default: () => createEmptyStop(1),
});

const canMoveLeft = computed(() => props.index > 0);
const canMoveRight = computed(() => props.index < props.totalStops - 1);

const icon = computed(() =>
  stop.value.number === 1
    ? 'mdi-truck-delivery-outline'
    : 'mdi-package-variant-closed',
);

const subtitle = computed(() => {
  if (stop.value.number === 1) return 'Pickup from';
  if (stop.value.warehouse) return 'Return';
  return 'Deliver to';
});

const contactModel = computed<ContactModel>({
  get: () => ({
    note_name: stop.value.note_name,
    note_phone: stop.value.note_phone,
  }),
  set: (value) => {
    stop.value = {
      ...stop.value,
      note_name: value.note_name,
      note_phone: value.note_phone,
    };
  },
});
</script>

<template>
  <div :class="$style.jobBookingStopCard" data-testid="jobBookingStopCard">
    <div :class="$style.header">
      <div :class="$style.iconBox">
        <v-icon :icon="icon" size="24" />
      </div>
      <div :class="$style.titleBlock">
        <h3 :class="$style.title">STOP {{ stop.number }}</h3>
        <p :class="$style.subtitle">{{ subtitle }}</p>
      </div>
      <div :class="$style.navArrows">
        <v-btn
          v-if="canMoveLeft"
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          @click="emit('moveLeft', $event)"
        />
        <v-btn
          v-if="canMoveRight"
          icon="mdi-arrow-right"
          variant="text"
          size="small"
          @click="emit('moveRight', $event)"
        />
        <v-btn
          v-if="props.deletable"
          icon="mdi-trash-can-outline"
          variant="text"
          size="small"
          color="error"
          aria-label="Remove stop"
          @click="emit('delete', $event)"
        />
      </div>
    </div>

    <StopCustomerWarehouseFieldset
      v-model="stop"
      :customer-code="props.customerCode"
      :address-items="props.addressItems"
      :address-loading="props.addressLoading"
    />
    <JobBookingWarningBanner
      v-if="props.interstate"
      message="Interstate route detected. Check before booking"
      icon="mdi-information"
    />
    <JobBookingWarningBanner
      v-if="props.customerToCustomer"
      message="The second leg of this job is a customer-to-customer delivery"
      icon="mdi-information"
    />

    <StopContactFieldset
      v-model="contactModel"
      :is-warehouse-stop="stop.warehouse"
    />
    <StopBookingDateTimeFieldset
      v-model="stop"
      :min-time="props.minTime"
      :hints="props.hints"
      @open-timeline="emit('openTimeline', $event)"
    />
    <StopAttributesFieldset
      :model-value="stop.attributes"
      @update:model-value="stop = { ...stop, attributes: $event }"
    />
    <KTextareaField
      :model-value="stop.note_text"
      label="Instructions"
      rows="3"
      @update:model-value="stop = { ...stop, note_text: $event ?? '' }"
    />
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.jobBookingStopCard {
  background: colors.$color-white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.iconBox {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: bp.$color-brand-100;
  color: bp.$color-brand-700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.titleBlock {
  flex: 1;
}

.navArrows {
  display: flex;
  gap: 4px;
  color: colors.$color-text-secondary;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: colors.$color-text-primary;
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 12px;
  color: colors.$color-text-secondary;
  margin: 2px 0 0;
  line-height: 1.2;
}

</style>
